import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';

import api, {
  setAccessToken,
  clearAccessToken,
} from '../services/api';

// =====================================
// CONTEXT
// =====================================

export const AuthContext = createContext(null);

// =====================================
// HOOK
// =====================================

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return ctx;
};

// =====================================
// PROVIDER
// =====================================

export function AuthProvider({ children }) {

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const didInit = useRef(false);

  useEffect(() => {

    if (didInit.current) return;
    didInit.current = true;

    const initAuth = async () => {
      try {
        const res = await api.post('/auth/refresh');

        const { accessToken, user } = res.data;

        setAccessToken(accessToken);
        setUser(user);
        setAuthError(null);

      } catch (err) {
        clearAccessToken();
        setUser(null);

        // =====================================
        // 🔥 TYDLIG FELHANTERING
        // =====================================

        if (!err.response) {
          // nätverk / server nere
          setAuthError('Kunde inte ansluta till servern.');
        } else if (err.response.status === 401) {
          // förväntat: ingen giltig session
          setAuthError(null);
        } else {
          // oväntat backend-fel
          setAuthError('Kunde inte logga in automatiskt.');
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();

  }, []);

  // =====================================
  // LOGIN
  // =====================================

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', {
        email,
        password,
      });

      const { accessToken, user } = res.data;

      setAccessToken(accessToken);
      setUser(user);
      setAuthError(null);

      return user;

    } catch (err) {
      throw err;
    }
  };

  // =====================================
  // LOGOUT
  // =====================================

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error(err);
    }

    clearAccessToken();
    setUser(null);
    setAuthError(null);
  };

  // =====================================
  // PROVIDER VALUE
  // =====================================

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        authError, // 👈 viktigt
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}